import api from '@/services/api.service';

export interface DocumentCollectionRequest {
  id: string;
  collection_number: string;
  delivery_note_id: string;
  sales_order_id: string;
  customer_id: string;
  status: string;
  priority: string;
  approval_status?: string | null;
  approval_instance_id?: string | null;
  assigned_controller_user_id?: string | null;
  delivery_date?: string | null;
  remarks?: string | null;
  approved_at?: string | null;
  customer?: { customer_name?: string };
  deliveryNote?: { delivery_note_number?: string; document_package_status?: string };
  workOrders?: { work_order_id: string; work_order_number?: string }[];
}

export interface DocumentCollectionItemRow {
  id: string;
  document_id: string;
  source_entity_type: string;
  source_entity_id: string;
  source_document_number?: string | null;
  category: string;
  included: boolean;
  is_auto_collected: boolean;
  is_additional_upload: boolean;
  remarks?: string | null;
  document?: {
    id: string;
    document_name: string;
    file_name: string;
    mime_type?: string;
    created_at?: string;
  };
}

export interface DocumentCollectionChecklistRow {
  id: string;
  checklist_code: string;
  checklist_label: string;
  status: string;
  blocks_approval: boolean;
  remarks?: string | null;
}

export interface DocumentCollectionTransmittal {
  customer_name: string | null;
  sales_order_number: string | null;
  delivery_note_number: string | null;
  work_orders: string[];
  collection_number: string;
  approved_at?: string | null;
  documents: {
    category: string;
    file_name?: string | null;
    source_document_number?: string | null;
    revision?: string | null;
  }[];
}

function peel<T>(body: unknown): T {
  if (!body || typeof body !== 'object') return body as T;
  const w = body as { data?: T };
  return (w.data !== undefined ? w.data : body) as T;
}

class DocumentCollectionService {
  async list(params?: Record<string, string>): Promise<DocumentCollectionRequest[]> {
    const q = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v) q.set(k, v);
      });
    }
    const res = await api.get(`/sales/document-collections?${q.toString()}`);
    return peel<DocumentCollectionRequest[]>(res) ?? [];
  }

  async getById(id: string): Promise<{
    request: DocumentCollectionRequest;
    items: DocumentCollectionItemRow[];
    checklist: DocumentCollectionChecklistRow[];
  }> {
    const res = await api.get(`/sales/document-collections/${id}`);
    return peel(res);
  }

  async recollect(id: string): Promise<{ added: number }> {
    const res = await api.post(`/sales/document-collections/${id}/recollect`);
    return peel(res);
  }

  async syncUploads(id: string): Promise<{ added: number }> {
    const res = await api.post(`/sales/document-collections/${id}/sync-uploads`);
    return peel(res);
  }

  async bulkUpdateItems(
    id: string,
    body: { included: boolean; all?: boolean; item_ids?: string[] }
  ): Promise<{ updated: number }> {
    const res = await api.patch(`/sales/document-collections/${id}/items/bulk`, body);
    return peel(res);
  }

  async createForDelivery(deliveryNoteId: string): Promise<DocumentCollectionRequest> {
    const res = await api.post(`/sales/deliveries/${deliveryNoteId}/document-collection`);
    return peel(res);
  }

  async updateItem(
    id: string,
    itemId: string,
    body: { included?: boolean; category?: string; remarks?: string }
  ): Promise<DocumentCollectionItemRow> {
    const res = await api.patch(`/sales/document-collections/${id}/items/${itemId}`, body);
    return peel(res);
  }

  async updateChecklist(
    id: string,
    checklistId: string,
    body: { status: string; remarks?: string }
  ): Promise<DocumentCollectionChecklistRow> {
    const res = await api.patch(`/sales/document-collections/${id}/checklist/${checklistId}`, body);
    return peel(res);
  }

  async submitForApproval(id: string): Promise<DocumentCollectionRequest> {
    const res = await api.post(`/sales/document-collections/${id}/submit-for-approval`);
    return peel(res);
  }

  async approve(id: string, remarks?: string): Promise<DocumentCollectionRequest> {
    const res = await api.post(`/sales/document-collections/${id}/approve`, { remarks });
    return peel(res);
  }

  async reject(id: string, body: Record<string, unknown>): Promise<DocumentCollectionRequest> {
    const res = await api.post(`/sales/document-collections/${id}/reject-approval`, body);
    return peel(res);
  }

  async getPackageByDelivery(deliveryNoteId: string) {
    const res = await api.get(`/sales/deliveries/${deliveryNoteId}/document-package`);
    return peel(res);
  }

  transmittalUrl(id: string): string {
    return `/sales/document-collections/${id}/transmittal`;
  }

  async downloadZip(id: string): Promise<Blob> {
    const res = await api.get(`/sales/document-collections/${id}/download-zip`, { responseType: 'blob' });
    return res as unknown as Blob;
  }

  async getTransmittal(id: string): Promise<DocumentCollectionTransmittal> {
    const res = await api.get(`/sales/document-collections/${id}/transmittal`);
    return peel<DocumentCollectionTransmittal>(res);
  }
}

export const documentCollectionService = new DocumentCollectionService();
export default documentCollectionService;
